import {
  PB_ACCOUNT_IFRAME_ID,
  PULSE_ALLOWED_ORIGINS,
  PULSE_BLAZOR_BASE_URL,
  PULSE_TEST_EVENT_NAME,
  buildPulseIframeUrlFromParentHash,
  createCancellableTimeout,
  findPulseIframeElement
} from './pbAccountIframeFeature'

export type PulseMessengerInstance = {
  synchronizeIFrameUrlWithCurrentWindowHashvalue?: (iframeId: string, baseUrl: string) => void
}

export type PulseTestEventHandler = (eventObj: CustomEvent<unknown>) => void

export type PulseMessengerModule = {
  CrossWinMessengerService: {
    GetSingleton: (config: unknown) => PulseMessengerInstance
  }
}

export type PulseBlazorIframeDeps = {
  logError: (error: unknown, context?: Record<string, unknown>) => void
  isClient: () => boolean
  loadMessengerModule: () => Promise<PulseMessengerModule>
  getPathname: () => string
  pulseBlazorBaseUrl?: string
}

type PulseBlazorIframeState = {
  messenger: PulseMessengerInstance | null
  urlSyncInitialized: boolean
  loadInFlight: Promise<PulseMessengerInstance | null> | null
}

export function createPulseBlazorIframeState (): PulseBlazorIframeState {
  return {
    messenger: null,
    urlSyncInitialized: false,
    loadInFlight: null
  }
}

export function createPulseBlazorIframeController (
  deps: PulseBlazorIframeDeps,
  state: PulseBlazorIframeState = createPulseBlazorIframeState()
) {
  const pulseBlazorBaseUrl = deps.pulseBlazorBaseUrl?.trim() || PULSE_BLAZOR_BASE_URL

  const loadPulseMessenger = async (): Promise<PulseMessengerInstance | null> => {
    if (!deps.isClient()) return null
    if (state.messenger) return state.messenger
    if (state.loadInFlight) return state.loadInFlight

    state.loadInFlight = deps.loadMessengerModule()
      .then((module) => {
        const CrossWinMessengerService = module?.CrossWinMessengerService
        if (!CrossWinMessengerService?.GetSingleton) {
          throw new Error('CrossWinMessengerService export missing GetSingleton')
        }

        state.messenger = CrossWinMessengerService.GetSingleton({
          myWindow: window,
          allowedOrigins: [...PULSE_ALLOWED_ORIGINS],
          resizeSyncEnabled: true
        })
        return state.messenger
      })
      .catch((error) => {
        state.loadInFlight = null
        deps.logError(error, {
          context: 'Pulse iframe: failed to load CrossWinMessengerService',
          route: deps.getPathname()
        })
        return null
      })

    return state.loadInFlight
  }

  /**
   * Initializes Pulse iframe URL/hash sync.
   * Returns a disposer that cancels deferred setup if the page unmounts first.
   */
  const initializePulseBlazorIframe = async (): Promise<() => void> => {
    if (!deps.isClient()) return () => {}

    const messenger = await loadPulseMessenger()
    if (!messenger) return () => {}

    // Deferred init matches Pulse PB.html semantics.
    return createCancellableTimeout(() => {
      try {
        const iframeElement = findPulseIframeElement(document, PB_ACCOUNT_IFRAME_ID)
        if (!iframeElement) {
          deps.logError(new Error('Pulse iframe element not found during initialization'), {
            context: 'Pulse iframe: DOM lookup failed',
            route: deps.getPathname()
          })
          return
        }

        if (!state.urlSyncInitialized) {
          messenger?.synchronizeIFrameUrlWithCurrentWindowHashvalue?.(
            PB_ACCOUNT_IFRAME_ID,
            pulseBlazorBaseUrl
          )
          state.urlSyncInitialized = true
          return
        }

        // Re-entry after remount: bootstrap blank iframe when hash/URL already matches cache.
        const targetUrl = buildPulseIframeUrlFromParentHash(
          pulseBlazorBaseUrl,
          window.location.hash || ''
        )
        if (!iframeElement.getAttribute('src')) {
          iframeElement.src = targetUrl
          return
        }

        window.dispatchEvent(new HashChangeEvent('hashchange'))
      } catch (error) {
        deps.logError(error, {
          context: 'Pulse iframe: synchronizeIFrameUrlWithCurrentWindowHashvalue failed',
          route: deps.getPathname()
        })
      }
    }, 0)
  }

  const addPulseTestEventListener = (handler: PulseTestEventHandler): (() => void) => {
    if (!deps.isClient()) return () => {}

    const listener: EventListener = (eventObj: Event) => {
      handler(eventObj as CustomEvent<unknown>)
    }

    window.addEventListener(PULSE_TEST_EVENT_NAME, listener)
    return () => {
      window.removeEventListener(PULSE_TEST_EVENT_NAME, listener)
    }
  }

  return {
    iframeID: PB_ACCOUNT_IFRAME_ID,
    pulseBlazorBaseUrl,
    allowedOrigins: [...PULSE_ALLOWED_ORIGINS],
    pulseTestEventName: PULSE_TEST_EVENT_NAME,
    initializePulseBlazorIframe,
    addPulseTestEventListener
  }
}
